// src/components/themes/default/store/branch/BranchProductDetailView.tsx
"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Image from "next/image";
import {
  ShoppingBag,
  ShieldCheck,
  RefreshCw,
  Truck,
  Rotate3d,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Check,
  Zap,
  Package,
  Layers,
  Star,
  ArrowLeft,
  Images as GalleryIcon,
} from "lucide-react";
import { useBranchCart } from "./BranchCartContext";
import BranchNavbar from "./BranchNavbar";
import BranchHeader from "./BranchHeader";
import BranchProductReviews from "./BranchProductReviews";

// 🌟 Robust Image URL Resolver (Localhost, Backend Media & Live Domain Sync)
function resolveProductImageUrl(item: any, subdomain: string = "nextlook"): string {
  if (!item) return "";
  let rawPath = "";

  if (typeof item === "string") {
    rawPath = item;
  } else if (typeof item === "object") {
    rawPath = item.image_url || item.image || item.url || item.file || item.media_file || "";
  }

  if (!rawPath || typeof rawPath !== "string") return "";

  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");

  const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";

  if (rawPath.startsWith("http://") || rawPath.startsWith("https://")) {
    if (
      typeof window !== "undefined" &&
      !window.location.hostname.includes("localhost") &&
      !window.location.hostname.includes("127.0.0.1")
    ) {
      try {
        const parsed = new URL(rawPath);
        if (parsed.hostname.includes("127.0.0.1") || parsed.hostname.includes("localhost")) {
          return parsed.pathname;
        }
      } catch {
        return rawPath;
      }
    }
    return rawPath;
  }

  let cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  if (!cleanPath.startsWith("/media/") && !cleanPath.startsWith("/static/")) {
    cleanPath = `/media${cleanPath}`;
  }

  return `${apiBase}${cleanPath}`;
}

function FormattedDescription({ content }: { content: string }) {
  if (!content) {
    return <p className="text-slate-400 text-xs">Ponyotir kono bistarito biboron dewa hoyni.</p>;
  }

  const lines = content.split("\n").map((l) => l.trim()).filter(Boolean);
  const introParagraphs: string[] = [];
  const bulletPoints: string[] = [];
  let isFeatureSection = false;

  lines.forEach((line) => {
    const isBullet =
      line.startsWith("•") ||
      line.startsWith("-") ||
      line.startsWith("*") ||
      line.startsWith("✓");

    const isHeader =
      line.includes("বৈশিষ্ট্য") ||
      line.includes("Features") ||
      line.includes("Highlights");

    if (isHeader) {
      isFeatureSection = true;
      return;
    }

    if (isBullet || isFeatureSection) {
      const cleanLine = line.replace(/^[•\-\*✓\s]+/, "").trim();
      if (cleanLine) bulletPoints.push(cleanLine);
    } else {
      introParagraphs.push(line);
    }
  });

  return (
    <div className="space-y-5 font-sans text-slate-700">
      {introParagraphs.length > 0 && (
        <div className="space-y-2.5">
          {introParagraphs.map((para, idx) => (
            <p
              key={idx}
              className={`text-xs sm:text-sm leading-relaxed ${
                idx === 0
                  ? "font-bold text-slate-900 text-sm sm:text-base border-l-4 border-emerald-600 pl-3 py-0.5"
                  : "text-slate-600"
              }`}
            >
              {para}
            </p>
          ))}
        </div>
      )}

      {bulletPoints.length > 0 && (
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 sm:p-5 space-y-3">
          <h4 className="text-xs sm:text-sm font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span>Mul boishishto o shubidhashomuho:</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            {bulletPoints.map((bullet, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2.5 p-2.5 rounded-xl bg-white border border-slate-200/70 text-xs sm:text-sm text-slate-800 shadow-2xs"
              >
                <div className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 stroke-[3]" />
                </div>
                <span className="leading-snug">{bullet}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface BranchProductDetailViewProps {
  product?: any;
  branch?: {
    id?: number;
    name?: string;
    slug?: string;
    contact_phone?: string;
    address?: string;
  } | null;
  subdomain?: string;
  primaryColor?: string;
  announcementText?: string;
  branchUser?: any;
  onOpenAuth?: () => void;
  onLogout?: () => void;
  onOpenCustomerDashboard?: () => void;
  onBack: () => void;
  onProceedToCheckout?: () => void;
}

export default function BranchProductDetailView({
  product = {},
  branch,
  subdomain = "nextlook",
  primaryColor = "#059669",
  announcementText,
  branchUser = null,
  onOpenAuth = () => {},
  onLogout = () => {},
  onOpenCustomerDashboard = () => {},
  onBack,
  onProceedToCheckout,
}: BranchProductDetailViewProps) {
  const { addToCart, setIsCartOpen } = useBranchCart();
  const [searchQuery, setSearchQuery] = useState("");

  const safeBranch = branch || {
    id: 0,
    name: "Branch Outlet",
    slug: "",
    contact_phone: "",
    address: "",
  };

  // 🌟 Local Storage theke Customer Session Auto-Recovery
  const [currentBranchUser, setCurrentBranchUser] = useState<any>(branchUser);

  useEffect(() => {
    if (branchUser) {
      setCurrentBranchUser(branchUser);
      return;
    }
    if (typeof window !== "undefined") {
      try {
        const bId = branch?.id || safeBranch.id;
        const saved =
          (bId && localStorage.getItem(`branch_customer_${bId}`)) ||
          localStorage.getItem("customer_user");
        if (saved) {
          setCurrentBranchUser(JSON.parse(saved));
        }
      } catch {}
    }
  }, [branchUser, branch?.id, safeBranch.id]);

  // 🌟 Backend theke live gallery images fetch korar state
  const [fetchedGalleryImages, setFetchedGalleryImages] = useState<any[]>([]);

  useEffect(() => {
    if (!product?.id) return;

    let isMounted = true;
    const fetchLiveGallery = async () => {
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const branchHeader = safeBranch.id ? { "X-Branch-ID": String(safeBranch.id) } : {};

        const res = await fetch(`${apiBase}/api/v1/products/${product.id}/images/`, {
          headers: {
            Accept: "application/json",
            ...branchHeader,
          },
        });

        if (res.ok && isMounted) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.results || [];
          if (list.length > 0) {
            setFetchedGalleryImages(list);
          }
        }
      } catch (err) {
        console.error("Gallery sync error:", err);
      }
    };

    fetchLiveGallery();

    return () => {
      isMounted = false;
    };
  }, [product?.id, safeBranch.id, subdomain]);

  // 🌟 Product Cover + Product Images + Live Uploaded Gallery Images Eksathe Merge
  const images = useMemo(() => {
    const rawList: any[] = [];

    // 1. Primary/Cover Image
    if (product?.primary_image) rawList.push(product.primary_image);
    if (product?.image) rawList.push(product.image);

    // 2. Inline product images from serializer
    const inlineImages = product?.images || product?.product_images || product?.productimage_set || [];
    if (Array.isArray(inlineImages)) {
      rawList.push(...inlineImages);
    }

    // 3. Live fetched gallery images from API
    if (fetchedGalleryImages.length > 0) {
      rawList.push(...fetchedGalleryImages);
    }

    // 4. Clean and resolve URLs
    const resolved = rawList
      .map((item) => resolveProductImageUrl(item, subdomain))
      .filter((url) => Boolean(url) && typeof url === "string");

    return Array.from(new Set(resolved));
  }, [fetchedGalleryImages, product, subdomain]);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string>(images[0] || "");
  const [isRotating, setIsRotating] = useState(false);
  const [autoRemoveBg, setAutoRemoveBg] = useState(false);
  const [processedImg, setProcessedImg] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<"desc" | "spec" | "reviews">("desc");

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (images.length > 0) {
      setSelectedImage(images[currentIdx] || images[0] || "");
    } else {
      setSelectedImage("");
    }
  }, [currentIdx, images]);

  useEffect(() => {
    if (!autoRemoveBg || !selectedImage) {
      setProcessedImg(null);
      return;
    }

    let isMounted = true;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = selectedImage;

    img.onload = () => {
      if (!isMounted) return;
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        ctx.drawImage(img, 0, 0);

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        const bgR = data[0];
        const bgG = data[1];
        const bgB = data[2];
        const threshold = 40;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          const dist = Math.sqrt(
            Math.pow(r - bgR, 2) + Math.pow(g - bgG, 2) + Math.pow(b - bgB, 2)
          );

          const isWhiteBg = r > 235 && g > 235 && b > 235;

          if (dist < threshold || isWhiteBg) {
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(imgData, 0, 0);
        setProcessedImg(canvas.toDataURL("image/png"));
      } catch {
        setProcessedImg(null);
      }
    };

    img.onerror = () => {
      if (isMounted) setProcessedImg(null);
    };

    return () => {
      isMounted = false;
    };
  }, [selectedImage, autoRemoveBg]);

  const scrollThumbnails = (dir: "left" | "right") => {
    if (thumbnailContainerRef.current) {
      const scrollAmount = dir === "left" ? -160 : 160;
      thumbnailContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const prevImage = () => {
    if (images.length === 0) return;
    setCurrentIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextImage = () => {
    if (images.length === 0) return;
    setCurrentIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = () => {
    if (!product || !product.id) return;
    addToCart(
      {
        id: product.id,
        name: product.name || "Product",
        price: Number(product.price || 0),
        image: selectedImage || product.primary_image || "",
        stock: product.stock_quantity ?? product.stock ?? 10,
      },
      quantity
    );
    setIsCartOpen(true);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };

  const handleBuyNow = () => {
    if (!product || !product.id) return;
    addToCart(
      {
        id: product.id,
        name: product.name || "Product",
        price: Number(product.price || 0),
        image: selectedImage || product.primary_image || "",
        stock: product.stock_quantity ?? product.stock ?? 10,
      },
      quantity
    );
    if (onProceedToCheckout) {
      onProceedToCheckout();
    }
  };

  const currentPrice = Number(product?.price || product?.sale_price || 0);
  const regularPrice = Number(product?.compare_price || product?.regular_price || 0);
  const hasDiscount = regularPrice > currentPrice && currentPrice > 0;
  const discountPercent =
    product?.discount_percentage ||
    (hasDiscount ? Math.round(((regularPrice - currentPrice) / regularPrice) * 100) : 0);

  const isInStock = (product?.stock_quantity ?? product?.stock ?? 1) > 0;
  const activeDisplaySrc = autoRemoveBg && processedImg ? processedImg : selectedImage;

  return (
    <div
      style={{ ["--branch-primary" as any]: primaryColor }}
      className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans pb-16 animate-fadeIn"
    >
      <BranchNavbar
        branch={safeBranch}
        announcementText={announcementText}
        primaryColor={primaryColor}
      />

      <BranchHeader
        branch={safeBranch}
        primaryColor={primaryColor}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onOpenAuth={onOpenAuth}
        branchUser={currentBranchUser}
        onLogout={onLogout}
        onOpenCustomerDashboard={onOpenCustomerDashboard}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 space-y-8 sm:space-y-12">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-2xl bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-all cursor-pointer shadow-2xs active:scale-95"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Products</span>
          </button>

          <nav className="text-xs text-slate-400 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap py-1">
            <button
              type="button"
              onClick={onBack}
              className="hover:text-emerald-600 transition-colors font-medium cursor-pointer"
            >
              {safeBranch.name}
            </button>
            <span>/</span>
            {product?.category_name && (
              <>
                <span className="text-slate-500 font-medium">{product.category_name}</span>
                <span>/</span>
              </>
            )}
            <span className="font-bold text-slate-800 truncate max-w-[180px] sm:max-w-none">
              {product?.name || "Product Details"}
            </span>
          </nav>
        </div>

        {/* Product Showcase Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Image Gallery Showcase */}
          <div className="lg:col-span-6 space-y-4">
            <div className="relative aspect-square w-full rounded-3xl bg-white border border-slate-200/90 overflow-hidden shadow-sm flex items-center justify-center p-4">
              <div className="absolute top-3 right-3 z-30 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsRotating(!isRotating)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isRotating
                      ? "bg-emerald-600 text-white ring-2 ring-emerald-400"
                      : "bg-white/95 hover:bg-white text-slate-700 border border-slate-200"
                  }`}
                >
                  <Rotate3d className={`w-3.5 h-3.5 ${isRotating ? "animate-spin" : ""}`} />
                  <span>3D View</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAutoRemoveBg(!autoRemoveBg)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    autoRemoveBg
                      ? "bg-amber-500 text-white ring-2 ring-amber-400"
                      : "bg-white/95 hover:bg-white text-slate-700 border border-slate-200"
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Clean BG</span>
                </button>
              </div>

              {activeDisplaySrc ? (
                <div
                  className={`relative w-full h-full flex items-center justify-center select-none ${
                    isRotating ? "animate-luxury-3d-turn" : ""
                  }`}
                >
                  <Image
                    src={activeDisplaySrc}
                    alt={product?.name || "Product Showcase"}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    quality={95}
                    unoptimized
                    priority
                    className="object-contain p-4 transition-all duration-300 drop-shadow-md"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-300 gap-2">
                  <ShoppingBag className="w-16 h-16 stroke-1" />
                  <span className="text-xs font-semibold">No Image Available</span>
                </div>
              )}

              {/* Prev/Next Image Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-md flex items-center justify-center cursor-pointer transition-all active:scale-95"
                    title="Previous Photo"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-md flex items-center justify-center cursor-pointer transition-all active:scale-95"
                    title="Next Photo"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* 🌟 Thumbnail Carousel for Gallery Images */}
            {images.length > 1 && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 px-1">
                  <span className="flex items-center gap-1.5 text-slate-600">
                    <GalleryIcon className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Product Gallery ({images.length} Photos)</span>
                  </span>
                  <span className="font-mono text-[11px]">
                    {currentIdx + 1} of {images.length}
                  </span>
                </div>

                <div className="relative flex items-center pt-1">
                  {images.length > 4 && (
                    <button
                      type="button"
                      onClick={() => scrollThumbnails("left")}
                      className="absolute -left-2 z-10 w-7 h-7 rounded-full bg-white text-slate-700 shadow-md flex items-center justify-center border border-slate-200 cursor-pointer hover:bg-slate-50"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                  )}

                  <div
                    ref={thumbnailContainerRef}
                    className="flex gap-3 overflow-x-auto scrollbar-none py-1 scroll-smooth w-full px-0.5"
                  >
                    {images.map((imgUrl: string, idx: number) => (
                      <button
                        key={imgUrl + idx}
                        type="button"
                        onClick={() => setCurrentIdx(idx)}
                        className={`relative w-16 h-16 sm:w-18 sm:h-18 rounded-2xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer bg-white p-1.5 flex items-center justify-center ${
                          currentIdx === idx
                            ? "border-emerald-600 ring-2 ring-emerald-500/20 shadow-xs scale-102"
                            : "border-slate-200 hover:border-slate-300 opacity-70 hover:opacity-100"
                        }`}
                      >
                        <Image
                          src={imgUrl}
                          alt={`Gallery Thumbnail ${idx + 1}`}
                          fill
                          sizes="72px"
                          unoptimized
                          className="object-contain p-1"
                        />
                      </button>
                    ))}
                  </div>

                  {images.length > 4 && (
                    <button
                      type="button"
                      onClick={() => scrollThumbnails("right")}
                      className="absolute -right-2 z-10 w-7 h-7 rounded-full bg-white text-slate-700 shadow-md flex items-center justify-center border border-slate-200 cursor-pointer hover:bg-slate-50"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Details & Actions */}
          <div className="lg:col-span-6 space-y-6">
            <div className="space-y-3 border-b border-slate-100 pb-5">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full ${
                    isInStock
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-600 border border-rose-200"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isInStock ? "bg-emerald-500" : "bg-rose-500"}`} />
                  {isInStock ? "In Stock" : "Stock Out"}
                </span>

                {product?.category_name && (
                  <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                    {product.category_name}
                  </span>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
                {product?.name || "Product Name"}
              </h1>

              {product?.sku && (
                <p className="text-xs text-slate-400 font-mono">
                  SKU: <span className="font-bold text-slate-600">{product.sku}</span>
                </p>
              )}

              <div className="flex items-baseline gap-3 pt-2">
                <span className="text-3xl sm:text-4xl font-black text-slate-900 font-mono">
                  ৳{currentPrice.toLocaleString()}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-base sm:text-lg text-slate-400 line-through font-mono">
                      ৳{regularPrice.toLocaleString()}
                    </span>
                    <span className="text-xs font-black text-emerald-700 bg-emerald-100/70 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                      {discountPercent}% OFF
                    </span>
                  </>
                )}
              </div>
            </div>

            {product?.description && (
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-3">
                {product.description.split("\n")[0].replace(/[•\-\*]/g, "")}
              </p>
            )}

            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-700">Poriman:</span>
                <div className="flex items-center border border-slate-200 rounded-2xl p-1 bg-white shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 cursor-pointer active:scale-90 transition-all"
                  >
                    -
                  </button>
                  <span className="w-10 text-center text-sm font-black text-slate-900 font-mono select-none">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-slate-600 hover:bg-slate-100 cursor-pointer active:scale-90 transition-all"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={!isInStock}
                  className={`w-full sm:flex-1 py-3.5 px-6 rounded-2xl font-bold text-xs sm:text-sm shadow-md flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 ${
                    isAdded
                      ? "bg-emerald-600 text-white"
                      : !isInStock
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/20"
                  }`}
                >
                  {isAdded ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
                  <span>{isAdded ? "Added to Cart" : "Add to Cart"}</span>
                </button>

                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={!isInStock}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full sm:flex-1 py-3.5 px-6 rounded-2xl font-bold text-xs sm:text-sm text-white shadow-md flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 hover:opacity-95"
                >
                  <Zap className="w-4 h-4 fill-white" />
                  <span>Buy Now (Order Korun)</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 p-4 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
              <div className="flex flex-col items-center text-center gap-1.5">
                <Truck className="w-5 h-5 text-emerald-600" />
                <div className="text-[11px]">
                  <strong className="block text-slate-800 font-bold">Outlet Express</strong>
                  <span className="text-slate-500 text-[10px]">Outlet theke druto delivery</span>
                </div>
              </div>
              <div className="flex flex-col items-center text-center gap-1.5 border-x border-slate-200">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                <div className="text-[11px]">
                  <strong className="block text-slate-800 font-bold">100% Authentic</strong>
                  <span className="text-slate-500 text-[10px]">Original fresh ponno</span>
                </div>
              </div>
              <div className="flex flex-col items-center text-center gap-1.5">
                <RefreshCw className="w-5 h-5 text-emerald-600" />
                <div className="text-[11px]">
                  <strong className="block text-slate-800 font-bold">Cash on Delivery</strong>
                  <span className="text-slate-500 text-[10px]">Dekhe newar shujog</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Sections */}
        <div className="pt-8 border-t border-slate-200">
          <div className="flex gap-4 sm:gap-8 border-b border-slate-200 pb-2 overflow-x-auto whitespace-nowrap">
            <button
              type="button"
              onClick={() => setActiveTab("desc")}
              className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === "desc"
                  ? "border-b-2 border-emerald-600 text-emerald-700"
                  : "text-slate-400 hover:text-slate-700"
              }`}
            >
              <Package className="w-4 h-4" />
              <span>Product Description</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("spec")}
              className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === "spec"
                  ? "border-b-2 border-emerald-600 text-emerald-700"
                  : "text-slate-400 hover:text-slate-700"
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>Specifications</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("reviews")}
              className={`text-sm font-black pb-3 -mb-2 transition-all cursor-pointer flex items-center gap-2 ${
                activeTab === "reviews"
                  ? "border-b-2 border-emerald-600 text-emerald-700"
                  : "text-slate-400 hover:text-slate-700"
              }`}
            >
              <Star className="w-4 h-4" />
              <span>Outlet Ratings</span>
            </button>
          </div>

          <div className="py-6 sm:py-8">
            {activeTab === "desc" && (
              <div className="max-w-4xl">
                <FormattedDescription content={product?.description || ""} />
              </div>
            )}

            {activeTab === "spec" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl text-xs sm:text-sm">
                <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                  <span className="text-slate-500 font-medium">Product Name</span>
                  <span className="font-bold text-slate-800">{product.name || "N/A"}</span>
                </div>
                <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                  <span className="text-slate-500 font-medium">Outlet Branch</span>
                  <span className="font-bold text-emerald-700">{safeBranch.name}</span>
                </div>
                {product?.category_name && (
                  <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                    <span className="text-slate-500 font-medium">Category</span>
                    <span className="font-bold text-slate-800">{product.category_name}</span>
                  </div>
                )}
                {product?.unit && (
                  <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                    <span className="text-slate-500 font-medium">Packaging Unit</span>
                    <span className="font-bold text-slate-800">{product.unit}</span>
                  </div>
                )}
                {product?.stock_quantity !== undefined && (
                  <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                    <span className="text-slate-500 font-medium">Branch Stock</span>
                    <span className="font-bold text-slate-800">{product.stock_quantity} available</span>
                  </div>
                )}
                {product?.sku && (
                  <div className="p-3.5 bg-white rounded-2xl flex justify-between border border-slate-200/80">
                    <span className="text-slate-500 font-medium">SKU</span>
                    <span className="font-mono font-bold text-slate-800">{product.sku}</span>
                  </div>
                )}
              </div>
            )}

            {activeTab === "reviews" && (
              <BranchProductReviews
                productId={product?.id}
                productName={product?.name}
                branchId={safeBranch.id}
                branchName={safeBranch.name}
                subdomain={subdomain}
                primaryColor={primaryColor}
                branchUser={currentBranchUser}
                onOpenAuth={onOpenAuth}
                isLight={true}
              />
            )}
          </div>
        </div>
      </div>

      <footer className="mt-16 border-t border-slate-200 bg-white py-8 px-4 text-center text-xs text-slate-500 space-y-2">
        <p className="font-bold text-slate-700">{safeBranch.name} — Branch Storefront</p>
        <p>
          {safeBranch.address || "Dhaka, Bangladesh"} • {safeBranch.contact_phone}
        </p>
        <p className="text-[10px] text-slate-400">Powered by Xylo SaaS Multi-Store Platform</p>
      </footer>
    </div>
  );
}